/**
 * Normalizes API strings for safe display: decodes HTML entities and strips invisible control characters.
 * Use when the backend may return encoded text (e.g. division names, CSAT labels).
 */
export function sanitizeDisplayText(input: string | number | null | undefined): string {
  if (input == null) return ''
  let s = String(input)
  // Zero-width spaces / BOM often appear in pasted or exported catalog data
  s = s.replace(/[\u200B-\u200D\uFEFF]/g, '')
  // C0 controls except tab/newline (labels are single-line; strip the rest)
  s = s.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '')
  s = s.trim()
  if (!s) return ''

  if (typeof document !== 'undefined') {
    const ta = document.createElement('textarea')
    ta.innerHTML = s
    s = ta.value.trim()
  } else {
    s = decodeBasicHtmlEntities(s)
  }
  return s
}

/**
 * Stricter sanitizer for labels/codes where symbols should be minimal
 * (e.g. division and CSAT labels).
 */
export function sanitizeRestrictedLabelText(input: string | number | null | undefined): string {
  const base = sanitizeDisplayText(input)
  if (!base) return ''
  // Keep letters, numbers, spaces, and common label/code punctuation only.
  const cleaned = base.replace(/[^A-Za-z0-9\s\-._,()\/&]/g, '').replace(/\s{2,}/g, ' ').trim()
  return cleaned
}

/** UI placeholder for empty table cells (not for CSV — Excel mojibakes UTF-8 em-dash as â€"). */
export const DISPLAY_EMPTY = '—'

export function isDisplayEmpty(value: string | null | undefined): boolean {
  if (value == null) return true
  const t = value.trim()
  return t === '' || t === DISPLAY_EMPTY || t === '–'
}

/** Normalize en/em dashes to ASCII hyphen for exports. */
export function normalizeUnicodeDashes(value: string): string {
  return value.replace(/\u2013|\u2014/g, '-')
}

/**
 * Value safe for analytics CSV: no em-dash placeholders, ASCII-friendly for Excel.
 */
export function formatAnalyticsCsvCell(
  value: string | number | null | undefined,
  options?: { restricted?: boolean }
): string {
  if (value == null) return ''
  if (typeof value === 'number') return String(value)
  let s = options?.restricted ? sanitizeRestrictedLabelText(value) : sanitizeDisplayText(String(value))
  if (isDisplayEmpty(s)) return ''
  s = normalizeUnicodeDashes(s)
  return s
}

/** Minimal decode when DOM is unavailable (e.g. tests). */
function decodeBasicHtmlEntities(str: string): string {
  return str
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&#x27;/gi, "'")
    .replace(/&#(\d+);/g, (full, code) => {
      const n = Number(code)
      if (!Number.isFinite(n) || n < 0 || n > 0x10ffff) return full
      try {
        return String.fromCodePoint(n)
      } catch {
        return full
      }
    })
    .replace(/&#x([0-9a-f]+);/gi, (full, hex) => {
      const n = parseInt(hex, 16)
      if (!Number.isFinite(n) || n < 0 || n > 0x10ffff) return full
      try {
        return String.fromCodePoint(n)
      } catch {
        return full
      }
    })
}
